/**
 * Pogo's DEMO catalog — the products and collections a grid falls back to when nothing real resolves.
 *
 * It lives in ui-react, not in the storefront app, for one reason: the BUILDER needs it too. A seller
 * opening Pogo on a store with no products would otherwise get the house fallback — a clothing shop —
 * inside a toy theme (spec 0032 §3 always listed `catalog` as one of a theme's four pieces).
 *
 * TYPE-ONLY IMPORTS on purpose: this module must stay free of any section import, because
 * `sections/_shared/product.ts` reads it. Anything else here would close an import cycle.
 *
 * Prices take the STORE's currency rather than carrying their own: the same demo has to read correctly
 * in a MAD shop and in a EUR one, and a demo that shows the wrong currency teaches the seller nothing.
 *
 * THE IDS ARE LOad-BEARING: `pogoHome.ts` pins four of them (`p-tilwa-ferme`, `-blocs`, `-arc-en-ciel`,
 * `-cuisine`) in the shoppable image's hotspots. Rename one here and the hotspot resolves to nothing —
 * hidden on a live store, sample product in the builder, and no error anywhere.
 */
const IMG = '/demo/toys';
function product(currency, p) {
    return {
        id: p.id,
        name: p.name,
        slug: p.slug,
        description: p.desc,
        price: { amount: p.price, currency },
        compareAtPrice: p.compareAt ? { amount: p.compareAt, currency } : null,
        images: [{ url: `${IMG}/${p.image}`, alt: p.alt, isCover: true }],
        inventory: { tracked: false, quantity: null },
        seo: { metaTitle: null, metaDescription: null },
        status: 'active',
        tags: p.badge ? [p.badge] : [],
        vendor: 'TILWA',
        rating: p.rating,
        ...(p.option
            ? { options: [{ name: p.option.name, values: p.option.values.map((label) => ({ label })) }] }
            : {}),
    };
}
/** The TILWA range, in merchandising order. */
export function pogoDemoProducts(currency) {
    return [
        product(currency, {
            id: 'p-tilwa-ferme',
            name: 'La Grande Ferme en bois',
            slug: 'grande-ferme-en-bois',
            price: 54900,
            compareAt: 69000,
            badge: 'custom-badge-1',
            image: 'product-farm.webp',
            alt: 'La Grande Ferme en bois et ses animaux sculptés',
            rating: { value: 4.9, count: 218 },
            desc: 'Grange à toit amovible, enclos et huit animaux sculptés. Dès 3 ans.',
            option: { name: 'Coffret', values: ['Ferme + 8 animaux', 'Ferme seule'] },
        }),
        product(currency, {
            id: 'p-tilwa-arc-en-ciel',
            name: 'Arc-en-ciel à empiler',
            slug: 'arc-en-ciel-a-empiler',
            price: 24900,
            compareAt: 29900,
            badge: 'custom-badge-2',
            image: 'product-stacker.webp',
            alt: 'Arc-en-ciel à empiler, sept arches en bois peint',
            rating: { value: 4.9, count: 291 },
            desc: 'Sept arches à empiler, aligner, renverser. Dès 12 mois.',
        }),
        product(currency, {
            id: 'p-tilwa-blocs',
            name: 'Blocs de construction — 48 pièces',
            slug: 'blocs-de-construction-48-pieces',
            price: 32900,
            image: 'product-blocks.webp',
            alt: 'Blocs de construction en érable dans leur sac en coton',
            rating: { value: 4.8, count: 164 },
            desc: 'Cubes, arches et triangles en érable, dans un sac en coton. Dès 18 mois.',
        }),
        product(currency, {
            id: 'p-tilwa-cuisine',
            name: 'Cuisine en bois',
            slug: 'cuisine-en-bois',
            price: 89900,
            compareAt: 99900,
            image: 'product-kitchen.webp',
            alt: 'Petite cuisine en bois avec évier, four et légumes en feutrine',
            rating: { value: 4.8, count: 76 },
            desc: 'Évier, four, plaques et légumes en feutrine. Dès 3 ans.',
        }),
        product(currency, {
            id: 'p-tilwa-canard',
            name: 'Canard à tirer',
            slug: 'canard-a-tirer',
            price: 13900,
            image: 'product-duck.webp',
            alt: 'Canard à tirer en bois sur ses roues',
            rating: { value: 4.9, count: 205 },
            desc: 'Il claque des ailes en roulant. Le compagnon des premiers pas. Dès 12 mois.',
        }),
        product(currency, {
            id: 'p-tilwa-poupee',
            name: 'Poupée de chiffon Nour',
            slug: 'poupee-de-chiffon-nour',
            price: 19900,
            image: 'product-doll.webp',
            alt: 'Poupée de chiffon en coton naturel, robe en lin',
            rating: { value: 4.9, count: 143 },
            desc: 'Coton naturel, cheveux en laine, visage brodé. Dès la naissance.',
        }),
        product(currency, {
            id: 'p-tilwa-formes',
            name: 'Boîte à formes',
            slug: 'boite-a-formes',
            price: 18900,
            image: 'product-shapes.webp',
            alt: 'Boîte à formes en bois et ses quatre formes colorées',
            rating: { value: 4.8, count: 97 },
            desc: 'Quatre formes, quatre trous, un tiroir qui se vide tout seul. Dès 18 mois.',
        }),
        product(currency, {
            id: 'p-tilwa-puzzle',
            name: 'Puzzle des animaux',
            slug: 'puzzle-des-animaux',
            price: 15900,
            image: 'product-puzzle.webp',
            alt: 'Puzzle en bois à encastrements avec lion, éléphant et girafe',
            rating: { value: 4.7, count: 132 },
            desc: 'Six encastrements à grosses poignées, pour les premières mains. Dès 18 mois.',
        }),
    ];
}
/** Pogo merchandises by AGE, not by product family — that is how a parent shops a toy shelf. */
export function pogoDemoCollections() {
    const make = (id, name, slug, cover, alt, productCount, description) => ({
        id,
        name,
        slug,
        description,
        coverImage: { url: `${IMG}/${cover}`, alt },
        productCount,
    });
    return [
        make('c-0-2', '0 – 2 ans', 'premiers-jouets', 'age-baby.webp', 'Canard à tirer et blocs en bois près des mains d’un bébé', 4, 'Attraper, lâcher, faire rouler. Grosses pièces, rien à avaler.'),
        make('c-3-5', '3 – 5 ans', 'jeu-symbolique', 'age-toddler.webp', 'Boîte à formes et arc-en-ciel à empiler', 4, 'L’âge où l’on rejoue le monde : la cuisine, la ferme, la dînette.'),
        make('c-6-plus', '6 ans et +', 'construire-et-imaginer', 'age-kid.webp', 'Ferme en bois et puzzle des animaux', 3, 'Construire, assembler, raconter des histoires plus longues.'),
        make('c-cadeaux', 'Coffrets cadeaux', 'coffrets-cadeaux', 'age-gift.webp', 'Coffret TILWA ouvert sur un arc-en-ciel à empiler, ruban en coton', 4, 'Emballés, avec votre message écrit à la main. Offert.'),
    ];
}
//# sourceMappingURL=catalog.js.map